import styled from '@emotion/styled';
import { AvailableDeliveryDueDate } from '@kaizenplatform/hugot-entity';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import Help from '@material-ui/icons/Help';
import { List } from 'immutable';
import { ClassAttributes, createElement, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import InputtedCreationSet from '../../../entity/InputtedCreationSet';
import Language from '../../../entity/Language';
import ProductPricing from '../../../entity/ProductPricing';
import { CreationSetForShowFragment, CreativeFormatForShowFragment } from '../../../generated/graphql';
import { toJSDate } from '../../../graphql/datetime';
import theme from '../../../theme';
import { calculateExpressPerDay, calculatePoint, calculateTotalPoint } from '../../../utility/calculatePoint';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { flexibleLayoutContainer, flexibleLayoutItem } from '../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import mq from '../../styleUtility/mediaquery';
import _AvailableDeliveryDueDateList from './AvailableDeliveryDueDateList';
import CreativeTypeLabel from './CreativeFormatCreativeTypeLabel';
import _NetworkIcon from './CreativeFormatNetworkIcon';
import CreativeFormatPlacementLabel from './CreativeFormatPlacementLabel';
import DeliveryDueDate from './DeliveryDueDate';

interface Props {
  creationSet: CreationSetForShowFragment;
  language?: Language;
  isPointRequired: boolean;
  productPricing?: ProductPricing;
  creationSetsInput?: List<InputtedCreationSet>;
  availableDeliveryDueDates?: AvailableDeliveryDueDate[];
  onChangeDeliveryDueDays?: (days: number | null, expressDueDays: number) => void;
  showCreationSetTitle?: boolean;
  className?: string;
}

const CreationSetList = ({
  creationSet,
  language,
  isPointRequired,
  productPricing,
  creationSetsInput,
  availableDeliveryDueDates,
  onChangeDeliveryDueDays,
  showCreationSetTitle = true,
  className,
}: ClassAttributes<HTMLElement> & Props) => {
  const creationSetDetail = (
    creationSet: CreationSetForShowFragment,
    creationSetInput?: InputtedCreationSet | null,
  ) => {
    const { creativeFormats, amount } = creationSet;
    const emptyCell = <DenserTableCell>-</DenserTableCell>;
    const expressDueDays = creationSetInput ? creationSetInput.expressDueDays : creationSet.expressDueDays;

    const expressSubTotal =
      isPointRequired && productPricing
        ? creativeFormats
            .map((creativeFormat) => calculateExpressPerDay(creativeFormat, expressDueDays))
            .reduce((accum: number, data: number) => {
              return accum + data;
            })
        : 0;
    const creativeSubTotal =
      isPointRequired && productPricing
        ? creativeFormats
            .map((creativeFormat) => calculatePoint(creativeFormat))
            .reduce((accum: number, data: number) => {
              return accum + data;
            })
        : 0;
    const pointSubtotal = isPointRequired && productPricing ? calculateTotalPoint(creationSet, expressDueDays) : 0;
    const subtotal = isPointRequired && productPricing ? pointSubtotal * productPricing.flatRate : 0;

    const sortedCreativeFormats: CreativeFormatForShowFragment[] = [];
    const resizeCreativeFormats = creativeFormats.filter((creativeFormat) => !!creativeFormat.resizeBaseId);
    creativeFormats.map((creativeFormat) => {
      if (!creativeFormat.resizeBaseId) {
        sortedCreativeFormats.push(creativeFormat);

        resizeCreativeFormats.map((resizeCreativeFormat) => {
          if (resizeCreativeFormat.resizeBaseId === creativeFormat.id) {
            sortedCreativeFormats.push(resizeCreativeFormat);
          }
        });
      }
    });

    return (
      <Table key={`creation_set_table_${creationSet.id}}`} className={className}>
        <TableHead>
          <TableRow>
            <DenserTableCell>
              <ColumnHeader>
                <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.network" />
              </ColumnHeader>
            </DenserTableCell>
            <DenserTableCell>
              <ColumnHeader>
                <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.placement" />
              </ColumnHeader>
            </DenserTableCell>
            <DenserTableCell>
              <ColumnHeader>
                <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.creative_type" />
              </ColumnHeader>
            </DenserTableCell>
            <DenserTableCell>
              <ColumnHeader>
                <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.size" />
              </ColumnHeader>
            </DenserTableCell>
            <DenserTableCell>
              <ColumnHeader>
                <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.length" />
              </ColumnHeader>
            </DenserTableCell>
            <DenserTableCell>
              <ColumnHeader>
                <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.option" />
              </ColumnHeader>
            </DenserTableCell>
            {isPointRequired && productPricing && (
              <DenserTableCell>
                <ColumnHeader>
                  <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.point" />
                </ColumnHeader>
              </DenserTableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedCreativeFormats.map((creativeFormat) => {
            const { network, placement, attribute, creativeType } = creativeFormat;

            const expressPerDay =
              isPointRequired && productPricing ? calculateExpressPerDay(creativeFormat, expressDueDays) : 0;
            const requiredPoint = isPointRequired && productPricing ? calculatePoint(creativeFormat) : 0;

            const networkCell = (
              <DenserTableCell>
                {!creativeFormat.resizeBaseId ? <NetworkIcon network={network} /> : null}
              </DenserTableCell>
            );
            const placementCell = (
              <DenserTableCell>
                {!creativeFormat.resizeBaseId ? (
                  <Placement>
                    <CreativeFormatPlacementLabel network={network} placement={placement} />
                  </Placement>
                ) : null}
              </DenserTableCell>
            );
            const creativeTypeCell = (
              <DenserTableCell>
                {!creativeFormat.resizeBaseId ? <CreativeTypeLabel creativeType={creativeType} /> : null}
              </DenserTableCell>
            );
            const optionCell = (
              <DenserTableCell>
                {creativeFormat.resizeBaseId ? (
                  <FormattedMessage id="creative_format_editor.resize_item.resize" />
                ) : null}
              </DenserTableCell>
            );

            const pointCell =
              isPointRequired && productPricing ? (
                <DenserTableCell>
                  <AlignCenter>
                    <RequiredPoint>
                      {productPricing.currency}
                      {(requiredPoint * productPricing.flatRate).toLocaleString()}
                      {expressPerDay ? (
                        <>
                          <RequiredPointUnit>+</RequiredPointUnit>
                          <ExpressPerDay>
                            {productPricing.currency}
                            {(expressPerDay * productPricing.flatRate).toLocaleString()}
                          </ExpressPerDay>
                        </>
                      ) : null}
                    </RequiredPoint>
                  </AlignCenter>
                </DenserTableCell>
              ) : null;

            const amountCell = null;

            const TableBodyRow = creativeFormat.resizeBaseId ? TableRow : TableRowBg;

            if (creativeType.key === 'video') {
              // TODO: inferred from creativeType
              return (
                <TableBodyRow key={`creation_set_table_row_${creativeFormat.id}`}>
                  {networkCell}
                  {placementCell}
                  {creativeTypeCell}
                  <DenserTableCell>
                    <Size>
                      {attribute.aspectRatio &&
                      attribute.aspectRatio.aspectRatio.width * attribute.aspectRatio.aspectRatio.height > 0 ? (
                        `${attribute.aspectRatio.aspectRatio.width}:${attribute.aspectRatio.aspectRatio.height} (${attribute.aspectRatio.size.width}x${attribute.aspectRatio.size.height})`
                      ) : (
                        <FormattedMessage id="creative_format.aspect_ratio.nullable" />
                      )}
                    </Size>
                  </DenserTableCell>
                  <DenserTableCell>
                    <Length>
                      {attribute.playbackTime ? (
                        <FormattedMessage
                          id="creative_format.playback_time"
                          values={{ seconds: attribute.playbackTime.seconds }}
                        />
                      ) : (
                        '-'
                      )}
                    </Length>
                  </DenserTableCell>
                  {optionCell}
                  {pointCell}
                  {amountCell}
                </TableBodyRow>
              );
            } else if (creativeType.key === 'static_image') {
              return (
                <TableBodyRow key={creativeFormat.id}>
                  {networkCell}
                  {placementCell}
                  {creativeTypeCell}
                  <DenserTableCell>
                    <Size>
                      {attribute.aspectRatio &&
                      attribute.aspectRatio.aspectRatio.width * attribute.aspectRatio.aspectRatio.height > 0 ? (
                        `${attribute.aspectRatio.aspectRatio.width}:${attribute.aspectRatio.aspectRatio.height} (${attribute.aspectRatio.size.width}x${attribute.aspectRatio.size.height})`
                      ) : (
                        <FormattedMessage id="creative_format.aspect_ratio.nullable" />
                      )}
                    </Size>
                  </DenserTableCell>
                  {emptyCell}
                  {optionCell}
                  {pointCell}
                  {amountCell}
                </TableBodyRow>
              );
            } else {
              return (
                <TableBodyRow key={creativeFormat.id}>
                  {networkCell}
                  {placementCell}
                  {creativeTypeCell}
                  {emptyCell}
                  {emptyCell}
                  {emptyCell}
                  {pointCell}
                  {amountCell}
                </TableBodyRow>
              );
            }
          })}
          {isPointRequired && productPricing && (
            <TableRow key={'creative-subtotal-row'}>
              <DenserTableCellWithoutBorder colSpan={5} />
              <DenserTableCellWithoutBorder style={{ textAlign: 'right' }}>
                <PointLabel>
                  <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.creative_subtotal" />
                </PointLabel>
              </DenserTableCellWithoutBorder>
              <DenserTableCellWithoutBorder>
                <AlignCenter>
                  <RequiredPoint>
                    {productPricing.currency}
                    {(creativeSubTotal * productPricing.flatRate).toLocaleString()}
                  </RequiredPoint>
                </AlignCenter>
              </DenserTableCellWithoutBorder>
            </TableRow>
          )}
          {isPointRequired && productPricing && expressDueDays ? (
            <TableRow key={'exrpess-subtotal-row'}>
              <DenserTableCellWithoutBorder colSpan={5} />
              <DenserTableCellWithoutBorder style={{ textAlign: 'right' }}>
                <PointLabel>
                  <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.express_subtotal" />
                </PointLabel>
              </DenserTableCellWithoutBorder>
              <DenserTableCellWithoutBorder>
                <AlignCenter>
                  <RequiredPoint>
                    {productPricing.currency}
                    {(expressSubTotal * productPricing.flatRate).toLocaleString()}
                  </RequiredPoint>
                </AlignCenter>
              </DenserTableCellWithoutBorder>
            </TableRow>
          ) : null}
          {isPointRequired && productPricing ? (
            <TableRow key={'amount-row'}>
              <DenserTableCellWithoutBorder colSpan={3} />
              <DenserTableCell style={{ textAlign: 'right' }} colSpan={3}>
                <PointLabel>
                  <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.amount" />
                </PointLabel>
              </DenserTableCell>
              <DenserTableCell>
                <AlignRight>
                  <Amount>{amount}</Amount>
                </AlignRight>
              </DenserTableCell>
            </TableRow>
          ) : (
            <TableRow key={'amount-row'}>
              <DenserTableCellWithoutBorder colSpan={5} style={{ textAlign: 'right' }}>
                <PointLabel>
                  <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.amount" />
                </PointLabel>
              </DenserTableCellWithoutBorder>
              <DenserTableCellWithoutBorder>
                <AlignRight>
                  <Amount>{amount}</Amount>
                </AlignRight>
              </DenserTableCellWithoutBorder>
            </TableRow>
          )}
          {isPointRequired && productPricing && (
            <TableRow key={'subtotal-row'}>
              <DenserTableCellWithoutBorder colSpan={5} />
              <DenserTableCellWithoutBorder style={{ textAlign: 'right' }}>
                <PointLabel>
                  <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.subtotal" />
                </PointLabel>
              </DenserTableCellWithoutBorder>
              <DenserTableCellWithoutBorder>
                <AlignCenter>
                  <RequiredPoint>
                    {productPricing.currency}
                    {subtotal.toLocaleString()}
                  </RequiredPoint>
                </AlignCenter>
              </DenserTableCellWithoutBorder>
            </TableRow>
          )}
        </TableBody>
      </Table>
    );
  };

  const creationSetInput = creationSetsInput
    ? creationSetsInput.find((creationSetInput) => creationSetInput.id === creationSet.id)
    : null;
  return (
    <Root>
      <CreationSetListItem key={`creation_set_section_${creationSet.id}}`}>
        {showCreationSetTitle && (
          <Title variant="subtitle1">
            <FormattedMessage id="creation_set_list.creation_set_list_item.title" />
          </Title>
        )}
        {!creationSetInput && creationSet.deliveryDueDate ? (
          <DeliveryDueDate dueDate={toJSDate(creationSet.deliveryDueDate)} />
        ) : null}
        {creationSetInput && availableDeliveryDueDates && onChangeDeliveryDueDays ? (
          <AvailableDeliveryDueDate>
            <AvailableDeliveryDueDateList
              isPointRequired={isPointRequired}
              availableDeliveryDueDates={availableDeliveryDueDates}
              deliveryDueDays={creationSetInput.deliveryDueDays}
              onMount={(days: number | null, expressDueDays: number) => onChangeDeliveryDueDays(days, expressDueDays)}
              onChange={(days: number | null, expressDueDays: number) => onChangeDeliveryDueDays(days, expressDueDays)}
            />
            {availableDeliveryDueDates.length && isPointRequired && language === 'ja-JP' ? (
              <HelpExpressDelivery>
                <FormattedMessage id="help.creation_list_delivery_due_days">
                  {(url: string) => (
                    <HelpAnchor href={url} target="_blank" rel="noopener noreferrer">
                      <HelpIcon />
                      <FormattedMessage id="creation_set_list.express_delivery.help" />
                    </HelpAnchor>
                  )}
                </FormattedMessage>
              </HelpExpressDelivery>
            ) : null}
          </AvailableDeliveryDueDate>
        ) : null}
        <Body>{creationSetDetail(creationSet, creationSetInput)}</Body>
      </CreationSetListItem>
    </Root>
  );
};

const Root = styled('div')({
  ...flexibleLayoutContainer({
    direction: 'column',
  }),
});

const TableRowBg = styled(TableRow)({
  backgroundColor: '#f2f6fa',
});

const CreationSetListItem = styled('div')({
  ...flexibleLayoutItem({}),
  ...gridLayoutContainer({
    columns: ['auto'],
    rows: ['auto', 'auto', 'auto'],
    areas: [['title'], ['.'], ['body']],
  }),
  marginBottom: `${SPACING_REGULAR}px`,
});

const Title = styled(Typography)({
  ...gridLayoutItem('title'),
  ...flexibleLayoutContainer({
    direction: 'column',
  }),
  marginBottom: SPACING_REGULAR,
  [mq('sp')]: {
    fontSize: 21,
    fontWeight: 700,
    marginBottom: SPACING_UNIT * 3,
  },
});
const Body = styled('div')({
  ...gridLayoutItem('body'),
  [mq('sp')]: {
    overflow: 'auto',
  },
});

const AlignCenter = styled('div')({
  textAlign: 'center',
});

const AlignRight = styled('div')({
  textAlign: 'right',
});

const iconStyle = {
  height: 24,
  width: 24,
};

const NetworkIcon = styled(_NetworkIcon)({ ...iconStyle });

const DenserTableCell = styled(TableCell)();
DenserTableCell.defaultProps = { padding: 'default' };

const DenserTableCellWithoutBorder = styled(DenserTableCell)({ borderBottom: 0 });

const ColumnHeader = styled(Typography)();
ColumnHeader.defaultProps = {
  variant: 'caption',
  color: 'textSecondary',
  noWrap: true,
};

const Placement = styled(Typography)({
  width: 140,
  wordBreak: 'break-word',
});
const Size = styled(Typography)({});
const Length = styled(Typography)();
Length.defaultProps = { noWrap: true };

const Amount = styled(Typography)({
  fontWeight: 700,
  fontSize: '1rem',
});

const RequiredPoint = styled(Typography)({
  fontSize: 16,
  textAlign: 'right',
});

const RequiredPointTotal = styled(Typography)({
  fontSize: 26,
  fontWeight: 500,
  textAlign: 'right',
});
RequiredPointTotal.defaultProps = { color: 'primary' };

const RequiredPointUnit = styled(Typography)({
  fontSize: 14,
  display: 'inline-block',
  fontWeight: 700,
  marginLeft: 2,
  marginRight: 2,
});
RequiredPointUnit.defaultProps = { color: 'textSecondary' };

const ExpressPerDay = styled('span')({
  marginLeft: 2,
});

const PointLabel = styled(Typography)({
  fontWeight: 700,
});

const AvailableDeliveryDueDate = styled('div')({
  ...gridLayoutContainer({
    columns: ['auto', 'auto'],
    rows: ['auto'],
    areas: [['deliveryDueDate', 'help']],
  }),
});

const AvailableDeliveryDueDateList = styled(_AvailableDeliveryDueDateList)({
  ...gridLayoutItem('deliveryDueDate'),
  marginBottom: SPACING_UNIT,
});

const HelpExpressDelivery = styled(Typography)({
  ...gridLayoutItem('help'),
  ...flexibleLayoutContainer({
    justifyContent: 'flex-end',
  }),
  marginBottom: SPACING_UNIT,
});

const HelpAnchor = styled('a')({
  ...gridLayoutItem('help'),
  display: 'inline-flex',
  alignItems: 'center',
  color: theme.palette.secondary.main,
  textDecoration: 'none',
});

const HelpIcon = styled(Help)({
  marginRight: 2,
});

export default CreationSetList;
